import { Link } from '@tanstack/react-router';

import { EmptyState } from '@/shared/components/EmptyState';
import { TasksMark } from '@/shared/components/emptyMarks';
import { ListChecksIcon, SearchXIcon } from 'lucide-react';

export interface TaskListEmptyStateProps {
  workspaceId: string;
  /**
   * What the reader has narrowed by, in words, when the list is empty *because*
   * of it. Empty array means the workspace itself holds no tasks.
   */
  activeFilters: readonly string[];
  onClearFilters: () => void;
}

/**
 * Why the list is empty — and the two reasons are never conflated.
 *
 * "This workspace has no tasks" and "nothing matches what you asked for" are
 * different facts with different remedies (FR-019). Showing the first when the
 * second is true tells a reader their workspace is empty when it is not;
 * showing the second when the first is true offers to clear filters nobody set.
 * The page is judged on getting this right, which is why the two messages are
 * asserted side by side in the tests rather than merely existing.
 *
 * Typographic with a line-art mark: the brand forbids illustrated empty states.
 *
 * NOTE: this copy is AI-drafted and the constitution asks for human review of UI
 * copy before merge.
 */
export function TaskListEmptyState({
  workspaceId,
  activeFilters,
  onClearFilters,
}: TaskListEmptyStateProps) {
  const narrowed = activeFilters.length > 0;
  const Icon = narrowed ? SearchXIcon : ListChecksIcon;

  return (
    <EmptyState
      icon={Icon}
      // The mark only when the workspace genuinely holds no task; a filter
      // that matched nothing keeps the search glyph, which says what happened.
      mark={narrowed ? undefined : <TasksMark />}
      description={
        narrowed ? (
          <>
            {/*
            Names what excluded things. "No tasks match" on its own leaves the
            reader to work out which of three filters is responsible.
          */}
            <p className="text-foreground max-w-prose text-sm font-medium">
              No tasks match {activeFilters.join(' + ')}
            </p>
            <p className="text-muted-foreground max-w-prose text-sm">
              This workspace has tasks — none of them answer this question.
            </p>
            {/*
            One control clearing all three. Three separate clears would make the
            reader undo a filter at a time to find out which one was wrong.
          */}
            <button
              type="button"
              onClick={onClearFilters}
              className="text-foreground hover:bg-accent focus-visible:ring-ring border-border rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:outline-none"
            >
              Clear all filters
            </button>
          </>
        ) : (
          <>
            <p className="text-foreground max-w-prose text-sm font-medium">
              No tasks yet
            </p>
            <p className="text-muted-foreground max-w-prose text-sm">
              Tasks are created on a board, and every task in this workspace
              will appear here once they are.
            </p>
            <Link
              to="/workspaces/$workspaceId/boards"
              params={{ workspaceId }}
              className="text-foreground hover:bg-accent focus-visible:ring-ring border-border rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:outline-none"
            >
              Go to boards
            </Link>
          </>
        )
      }
    />
  );
}
